import { useCallback, useEffect, useState } from "react";
import { Map as MapIcon, Navigation, LocateFixed, ChevronDown, ChevronUp, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RouteMap } from "@/components/RouteMap";
import { api, errMsg } from "@/lib/api";
import { itemsText, todayIST } from "@/lib/format";

const gmapsUrl = (start, stops) => {
  const pts = stops.slice(0, 10).map((s) => `${s.lat},${s.lng}`);
  if (!pts.length) return null;
  const dest = pts.pop();
  return `https://www.google.com/maps/dir/?api=1&origin=${start.lat},${start.lng}&destination=${dest}&travelmode=driving${pts.length ? `&waypoints=${encodeURIComponent(pts.join("|"))}` : ""}`;
};

function FifoHint() {
  const [lots, setLots] = useState([]);
  useEffect(() => { api.get("/stock/lots").then((r) => setLots(r.data.filter((l) => l.state !== "expired").slice(0, 3))).catch(() => {}); }, []);
  if (!lots.length) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="fifo-hint">
      <div className="flex items-center gap-1 font-semibold text-amber-800"><PackageCheck className="h-4 w-4" />Carry older batches first</div>
      {lots.map((l) => <div key={l.id} className="mt-1">{l.code} · {l.name} · {l.qty_left} left · use by {l.expires_on}</div>)}
    </div>
  );
}

export function RoutePanel({ date, mine = false, defaultOpen = true }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const load = useCallback(() => api.get("/deliveries/route", { params: { date, mine } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [date, mine]);
  useEffect(() => { load(); }, [load]);
  const saveHere = (s) => {
    if (!navigator.geolocation) return toast.error("Location not available on this phone");
    navigator.geolocation.getCurrentPosition(
      async (p) => { try { await api.post(`/customers/${s.customer_id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }); toast.success(`Saved location for ${s.name}`); load(); } catch (e) { toast.error(errMsg(e)); } },
      () => toast.error("Please allow location access"), { enableHighAccuracy: true, timeout: 15000 });
  };
  if (!data) return null;
  const pending = data.stops.filter((s) => s.delivery_status === "pending");
  const url = gmapsUrl(data.start, pending);
  return (
    <div className="my-4 space-y-3 rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid="route-panel">
      <button className="flex w-full items-center justify-between" onClick={() => setOpen(!open)} data-testid="route-toggle">
        <span className="flex items-center gap-2 font-display font-semibold text-[#0F382C]"><MapIcon className="h-5 w-5" />{date === todayIST(1) ? "Tomorrow's" : date === todayIST() ? "Today's" : "Route"}{date === todayIST(1) || date === todayIST() ? " route" : ""} · {data.stops.length} stops · {data.total_km} km</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && <>
        <FifoHint />
        {data.stops.length > 0 && <RouteMap start={data.start} stops={data.stops} />}
        {url && <Button asChild className="w-full bg-[#0F382C] hover:bg-[#134E3A]"><a href={url} target="_blank" rel="noreferrer" data-testid="route-gmaps-btn"><Navigation className="mr-2 h-4 w-4" />Navigate next {Math.min(pending.length, 10)} stops in Google Maps</a></Button>}
        <ol className="space-y-1 text-sm" data-testid="route-order-list">
          {data.stops.map((s) => (
            <li key={s.order_id} className={`flex justify-between gap-2 ${s.delivery_status !== "pending" ? "text-muted-foreground line-through" : ""}`} data-testid={`route-stop-${s.seq}`}>
              <span><b className="font-mono">{s.seq}.</b> {s.name} <span className="text-xs text-muted-foreground">{s.area}</span>{s.slot && <span className="ml-1 rounded-full bg-[#FAF4E8] px-2 py-0.5 text-[11px] font-semibold text-[#0F382C]" data-testid={`route-slot-${s.seq}`}>{s.slot}</span>}</span>
              <span className="font-mono text-xs">{s.leg_km} km</span>
            </li>
          ))}
        </ol>
        {data.unlocated.length > 0 && (
          <div className="rounded-xl bg-[#FAF4E8] p-3 text-sm" data-testid="route-unlocated">
            <div className="font-semibold">Not on map yet ({data.unlocated.length})</div>
            <div className="text-xs text-muted-foreground mb-2">At the customer's door, tap "Save location" once. They'll be routed automatically from then on.</div>
            {data.unlocated.map((s) => (
              <div key={s.order_id} className="flex items-center justify-between gap-2 py-1">
                <span>{s.name} <span className="text-xs text-muted-foreground">{s.area}{s.slot ? ` · ${s.slot}` : ""} · {itemsText(s.items)}</span></span>
                <Button size="sm" variant="outline" onClick={() => saveHere(s)} data-testid={`save-location-${s.customer_id}`}><LocateFixed className="mr-1 h-4 w-4" />Save location</Button>
              </div>
            ))}
          </div>
        )}
      </>}
    </div>
  );
}
